// Single source of truth for semantic UI colors.
// Use C.xxx in inline JSX styles; use var(--abi-xxx) in CSS/className contexts.
export const C = {
  primary:  '#2563eb',
  accent:   '#7c3aed',
  provider: '#059669',
  danger:   '#ef4444',
  success:  '#10b981',
  warning:  '#f59e0b',
  info:     '#3b82f6',
};

// Order status badge styles (id 1 = pending, 2 = customer-approved, 3 = provider-approved)
export const STATUS_STYLE = {
  1: { background: '#fef9c3', color: '#854d0e', border: '1px solid #fde047' },
  2: { background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd' },
  3: { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' },
};

// Widget health-status color tokens (ok / warning / danger)
export const STATUS_COLORS = {
  ok:      { bg: '#ecfdf5', border: `${C.success}33`, dot: C.success, text: '#064e3b' },
  warning: { bg: '#fffbeb', border: `${C.warning}33`, dot: C.warning, text: '#92400e' },
  danger:  { bg: '#fff1f2', border: `${C.danger}33`,  dot: C.danger,  text: '#7f1d1d' },
};

// Fund-consumption bar color based on % used
export function barColor(pct) {
  if (pct >= 80) return C.danger;
  if (pct >= 50) return C.warning;
  return C.success;
}

// Configurable 3-state semaphore (verde / azul / rojo) for fund consumption,
// gated by an organization's `budget_semaphore` feature flag
// (features.budget_semaphore = { enabled, warning, danger }, thresholds as
// fractions e.g. 0.60 / 0.85). When the flag isn't enabled for the org,
// falls back to the existing barColor() behavior — zero change for tenants
// that don't have this compliance requirement.
export function semaphoreColor(pct, features) {
  const cfg = features && features.budget_semaphore;
  if (!cfg || !cfg.enabled) {
    return barColor(pct);
  }
  // Se decide con el mismo numero redondeado que se muestra en pantalla
  // (los llamadores siempre imprimen pct.toFixed(0)) para que el color
  // nunca contradiga la etiqueta.
  //
  // Umbrales tal como los exige el pliego (texto literal, requisito
  // tecnico N4): "cuando el presupuesto ejecutado SEA DEL 60% muestre
  // VERDE" / "entre un 61% y un 85% AZUL" / "en un 86% ROJO" — el 60%
  // es el limite superior de verde (no el piso de azul), por eso la
  // comparacion es estrictamente mayor (>), no >=.
  const roundedPct = Math.round(pct);
  const warningPct = (cfg.warning ?? 0.60) * 100;
  const dangerPct = (cfg.danger ?? 0.85) * 100;
  if (roundedPct > dangerPct) return C.danger;
  if (roundedPct > warningPct) return C.info;
  return C.success;
}

// Same thresholds as semaphoreColor(), returned as a state key for badges/
// labels ('ok' | 'info' | 'danger'). Falls back to the 2-tier
// STATUS_COLORS-compatible states ('ok' | 'warning' | 'danger') when the
// feature flag isn't enabled.
export function semaphoreState(pct, features) {
  const cfg = features && features.budget_semaphore;
  if (!cfg || !cfg.enabled) {
    if (pct >= 80) return 'danger';
    if (pct >= 50) return 'warning';
    return 'ok';
  }
  const roundedPct = Math.round(pct);
  const warningPct = (cfg.warning ?? 0.60) * 100;
  const dangerPct = (cfg.danger ?? 0.85) * 100;
  if (roundedPct > dangerPct) return 'danger';
  if (roundedPct > warningPct) return 'info';
  return 'ok';
}
